import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSave, faEye, faEyeSlash, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip, Modal, Button } from 'react-bootstrap';
import { createAdmin, updateAdmin, fetchAdmin, fetchAdmins } from '../../../../store/slices/adminSlice';
import { fetchPractice } from '../../../../store/slices/practiceSlice';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import adminService from '../../../../services/adminService';
import { toast } from 'react-toastify';
import './AddAdmin.css';
import { validatePassword } from '../../../../utils/passwordValidation';
import { TIME_ZONES, TITLES, US_STATES, normalizeTimeZone } from '../../../../utils/constants';
import AddFormModel from '../../../../components/Common/AddFormModel/AddFormModel';
import { buildPersonFormRows } from '../../../../components/Common/AddFormModel/personFormRows';
import FormActionButtons from '../../../../components/Common/FormActionButtons/FormActionButtons';
import FormDetailSkeleton from '../../../../components/SkeletonLoader/FormDetailSkeleton';

const AddAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { practiceId } = useParams();
  const [searchParams] = useSearchParams();

  const { isCreating, isUpdating, error, currentAdmin } = useSelector(state => state.admin);
  const { currentPractice } = useSelector(state => state.practice ?? {});
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [systemAdmin, setSystemAdmin] = useState(null);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [showValidationModal, setShowValidationModal] = useState(false);

  // Check if we're in edit mode
  const editId = searchParams.get('editId');
  const isEditMode = Boolean(editId);
  const isSystemAdmin = !practiceId; // System admin if no practiceId in route

  // Fetch admin data when in edit mode
  useEffect(() => {
    if (isEditMode && editId) {
      if (isSystemAdmin) {
        // Fetch system admin
        setLoadingAdmin(true);
        adminService.getSystemAdmin(editId)
          .then(response => {
            if (response.data && response.data.success) {
              setSystemAdmin(response.data.data);
            }
          })
          .catch(error => {
            console.error('Error fetching system admin:', error);
          })
          .finally(() => {
            setLoadingAdmin(false);
          });
      } else {
        // Fetch practice admin
        dispatch(fetchAdmin({ practiceId, adminId: editId }));
      }
    }
  }, [isEditMode, editId, practiceId, isSystemAdmin, dispatch]);

  // Fetch practice data if not already state
  useEffect(() => {
    if (practiceId && (!currentPractice || String(currentPractice.id) !== String(practiceId))) {
      dispatch(fetchPractice(practiceId));
    }
  }, [practiceId, currentPractice, dispatch]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  useEffect(() => {
    if (isSystemAdmin) {
      updateBreadcrumbs([
        { label: 'Admin', path: '/admin' },
        { label: isEditMode ? 'Edit System Admin' : 'Add System Admin', path: null, active: true }
      ]);
    } else {
      const practiceName = currentPractice?.practice_name || 'Practice';
      updateBreadcrumbs([
        { label: 'Practices', path: '/practices' },
        { label: practiceName, path: `/practices/${practiceId}?tab=practice` },
        { label: isEditMode ? 'Edit Practice Admin' : 'Add Practice Admin', path: null, active: true }
      ]);
    }
  }, [isSystemAdmin, isEditMode, practiceId, currentPractice, updateBreadcrumbs]);

  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    middleInitial: '',
    email: '',
    phone: '',
    cellPhone: '',
    fax: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '', // Full Name
    stateId: null, // backend ID
    zipCode: '',
    username: '',
    password: '',
    confirmPassword: '',
    status: 'Active',
    twoWayAuth: 'Disabled',
    timeZone: ''
  });

  const [initialFormData, setInitialFormData] = useState(null);

  const isChanged = React.useMemo(() => {
    if (!isEditMode || !initialFormData) return true; // Always enabled in create mode
    if (formData.password?.trim() || formData.confirmPassword?.trim()) return true;
    // Compare formData with initialFormData (excluding passwords which are handled separately)
    return JSON.stringify({ ...formData, password: '', confirmPassword: '' }) !== 
           JSON.stringify({ ...initialFormData, password: '', confirmPassword: '' });
  }, [formData, initialFormData, isEditMode]);
  const hasPasswordReuseError = String(errors.password || '').toLowerCase().includes('different from current password');

  // Populate form data when admin is loaded
  useEffect(() => {
    if (isEditMode) {
      const adminData = isSystemAdmin ? systemAdmin : currentAdmin;
      if (adminData) {
        const mappedData = {
          title: (adminData.title_id !== null && adminData.title_id !== undefined) 
            ? String(adminData.title_id) 
            : (TITLES.find(t => t.label === adminData.title)?.id ? String(TITLES.find(t => t.label === adminData.title).id) : ''),
          firstName: adminData.first_name || '',
          lastName: adminData.last_name || '',
          middleInitial: adminData.middle_name || adminData.middle_initial || '',
          email: adminData.email || '',
          addressLine1: adminData.address_line_1 || '',
          addressLine2: adminData.address_line_2 || '',
          city: adminData.city || '',
          state: US_STATES.find(s => s.id === adminData.state_id)?.name || '',
          stateId: adminData.state_id || null,
          zipCode: adminData.zip_code ? String(adminData.zip_code) : '',
          phone: formatPhoneNumber(adminData.phone || adminData.home_phone || ''),
          cellPhone: formatPhoneNumber(adminData.cell_phone || ''),
          fax: formatPhoneNumber(adminData.fax || ''),
          username: adminData.username || adminData.user?.username || '',
          status: adminData.status || 'Active',
          twoWayAuth: (adminData.is_two_way_auth === 1 || adminData.is_two_way_auth === true || adminData.two_way_auth === 'Enabled' || adminData.two_way_authentication === 'Enabled') ? 'Enabled' : 'Disabled',
          timeZone: normalizeTimeZone(adminData.time_zone) || '',
          password: '',
          confirmPassword: ''
        };
        setFormData(mappedData);
        setInitialFormData(mappedData);
      }
    }
  }, [isEditMode, currentAdmin, systemAdmin, isSystemAdmin]);
  // Helper for phone formatting (XXX) XXX - XXXX
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)} - ${phoneNumber.slice(6, 10)}`;
  };

  // Helper for email validation
  const validateEmail = (email) => {
    return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newErrors = { ...errors };

    // Clear existing error for current field before re-validating
    delete newErrors[name];

    // 1. Phone Number Masking & Validation
    if (['phone', 'cellPhone', 'fax'].includes(name)) {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        const formattedValue = formatPhoneNumber(numericValue);
        setFormData(prev => ({ ...prev, [name]: formattedValue }));
        if (name === 'phone' && numericValue.length === 0) {
          newErrors.phone = 'Home Phone is required';
        } else if (numericValue.length > 0 && numericValue.length < 10 && name !== 'fax') {
          newErrors[name] = 'Invalid phone format';
        } else {
          delete newErrors[name];
        }
        setErrors(newErrors);
      }
      return;
    }

    // 1.1 Zip Code Validation (Prevention)
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        if (numericValue.length > 0 && numericValue.length < 2) {
          newErrors.zipCode = 'Zip code must be at least 2 characters';
        } else {
          delete newErrors.zipCode;
        }
        setErrors(newErrors);
      }
      return;
    }

    // 2. Strict Name Validation
    if (['firstName', 'lastName', 'middleInitial'].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return; 
      }
      if ((name === 'firstName' || name === 'lastName') && value.length > 20) {
        return; 
      }
      if (name === 'middleInitial') {
        if (value.includes(' ')) {
          newErrors.middleInitial = 'Middle Initial cannot contain spaces';
        } else {
          delete newErrors.middleInitial;
        }
      }
    }

    // 2.1 State Sync
    if (name === 'state') {
      const selectedState = US_STATES.find(s => s.name === value);
      setFormData(prev => ({
        ...prev,
        state: value,
        stateId: selectedState ? selectedState.id : null
      }));
      return;
    }

    // 3. Password Validation (Real-time)
    if (name === 'password') {
      if (!value) {
        setErrors(prev => {
          const { password, ...rest } = prev;
          return rest;
        });
      } else {
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName
        };
        const validation = validatePassword(value, userData);
        if (!validation.isValid) {
          setErrors(prev => ({ ...prev, password: validation.error }));
        } else {
          setErrors(prev => {
            const { password, ...rest } = prev;
            return rest;
          });
        }
      }
    }

    // City Validation (Alpha only)
    if (name === 'city') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // 4. Email Validation (Real-time + Character Filtering)
    if (name === 'email') {
      const cleanedValue = value.replace(/\s/g, '');
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      
      setFormData(prev => ({ ...prev, email: cleanedValue }));
      
      if (!cleanedValue) {
        setErrors(prev => ({ ...prev, email: 'Email is required' }));
      } else if (cleanedValue && !emailRegex.test(cleanedValue)) {
        setErrors(prev => ({ ...prev, email: 'Invalid email format' }));
      } else {
        setErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
      }
      return; // Handled update
    }

    // Username validation (alphanumeric only)
    if (name === 'username') {
      const cleanedValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setFormData(prev => ({ ...prev, username: cleanedValue }));
      if (!cleanedValue) {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      } else if (!/^[a-zA-Z0-9]+$/.test(cleanedValue)) {
        setErrors(prev => ({ ...prev, username: 'Username should only contain alphanumeric characters' }));
      } else {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      }
      return;
    }

    if (name === 'confirmPassword') {
      if (!value && !isEditMode) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (formData.password && value !== formData.password) {
        newErrors.confirmPassword = 'Passwords do not match';
      } else {
        delete newErrors.confirmPassword;
      }
    }

    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      username: 'Username',
      addressLine1: 'Address Line 1',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      timeZone: 'Time Zone',
      twoWayAuth: 'Two Way Authentication'
    };
    if (fieldLabels[name] && (!value || !String(value).trim())) {
      newErrors[name] = `${fieldLabels[name]} is required`;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name !== 'password' && name !== 'email') {
      setErrors(newErrors);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';
    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else if (!/^[a-zA-Z0-9]+$/.test(formData.username.trim())) {
      newErrors.username = 'Username should only contain alphanumeric characters';
    }
    if (!formData.timeZone) newErrors.timeZone = 'Time Zone is required';
    if (!formData.twoWayAuth) newErrors.twoWayAuth = 'Two Way Authentication is required';

    // Password validation
    if (!isEditMode) {
      if (!formData.password?.trim()) {
        newErrors.password = 'Password is required';
      } else {
        const userData = { firstName: formData.firstName, lastName: formData.lastName };
        const validation = validatePassword(formData.password, userData);
        if (!validation.isValid) newErrors.password = validation.error;
      }
    } else if (formData.password?.trim()) {
      const userData = { firstName: formData.firstName, lastName: formData.lastName };
      const validation = validatePassword(formData.password, userData);
      if (!validation.isValid) newErrors.password = validation.error;
    }

    // Confirm Password validation
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!formData.city?.trim()) newErrors.city = 'City is required';
    if (!formData.state || formData.state === 'Select') newErrors.state = 'State is required';
    
    if (!formData.zipCode?.trim()) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (formData.zipCode.trim().length < 2) {
      newErrors.zipCode = 'Zip code must be at least 2 characters';
    }

    const emailValue = (formData.email || '').trim();
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    if (!emailValue) {
      newErrors.email = 'Email is required';
    } else if (/\s/.test(formData.email)) {
      newErrors.email = 'Email cannot contain spaces';
    } else if (!emailRegex.test(emailValue)) {
      newErrors.email = 'Invalid email format';
    }

    const numericHome = (formData.phone || '').replace(/\D/g, '');
    if (!numericHome) {
      newErrors.phone = 'Home Phone is required';
    } else if (numericHome.length < 10) {
      newErrors.phone = 'Home Phone must be exactly 10 digits';
    }

    const numericCell = (formData.cellPhone || '').replace(/\D/g, '');
    if (numericCell && numericCell.length < 10) {
      newErrors.cellPhone = 'Cell Phone must be exactly 10 digits';
    }

    const numericFax = (formData.fax || '').replace(/\D/g, '');
    if (numericFax && numericFax.length < 10) {
      newErrors.fax = 'Fax must be exactly 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      setShowValidationModal(true);
      return;
    }
      try {
        let result;
        // Filter out fields that the backend doesn't expect
        const filteredFormData = {
          first_name: formData.firstName || '',
          last_name: formData.lastName || '',
          middle_name: formData.middleInitial || '',
          email: formData.email || '',
          phone: formData.phone || '',
          cell_phone: formData.cellPhone || '',
          fax: formData.fax || '',
          address_line_1: formData.addressLine1 || '',
          address_line_2: formData.addressLine2 || '',
          city: formData.city || '',
          state_id: (formData.stateId !== undefined && formData.stateId !== null && formData.stateId !== '')
            ? parseInt(formData.stateId)
            : 1,
          zip_code: formData.zipCode ? String(formData.zipCode) : '',
          username: formData.username || '',
          status: formData.status || 'Active',
          two_way_auth: formData.twoWayAuth || 'Disabled',
          time_zone: formData.timeZone,
          title_id: parseInt(formData.title) || null
        };

        if (formData.password && formData.password.trim() !== '') {
          filteredFormData.password = formData.password;
        }

        console.log('🔧 Filtered form data before sending:', filteredFormData);

        if (isEditMode) {
          result = await dispatch(updateAdmin({ 
            practiceId: isSystemAdmin ? null : practiceId, 
            adminId: editId, 
            adminData: filteredFormData 
          })).unwrap();
        } else {
          result = await dispatch(createAdmin({ 
            practiceId: isSystemAdmin ? null : practiceId, 
            adminData: {
              ...filteredFormData,
              password: formData.password || ''
            } 
          })).unwrap();
        }

        if (isSystemAdmin) {
          toast.success(isEditMode ? 'System admin updated' : 'System admin added');
          navigate('/admin');
        } else {
          toast.success(isEditMode ? 'Practice admin updated' : 'Practice admin added');
          navigate(`/practices/${practiceId}?tab=admin`);
        }
      } catch (error) {
        console.error(`Error ${isEditMode ? 'updating' : 'creating'} admin:`, error);
        const errorMessage = typeof error === 'string'
          ? error
          : (error?.response?.data?.message || error?.message || 'Failed to save admin. Please try again.');
        if (String(errorMessage).toLowerCase().includes('different from current password')) {
          setErrors(prev => ({ ...prev, password: errorMessage }));
        }
        toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
  };

  const handleCancel = () => {
    if (isSystemAdmin) {
      navigate('/admin');
    } else {
      navigate(`/practices/${practiceId}?tab=admin`);
    }
  };

  // Get logo URL - construct full URL if logo path exists
  const getLogoUrl = () => {
    if (!currentPractice?.practice_logo) return null;
    const logo = currentPractice.practice_logo;
    if (logo.startsWith('http://') || logo.startsWith('https://')) return logo;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const baseUrl = API_URL.replace('/api', '');
    const logoPath = logo.startsWith('/') ? logo : `/${logo}`;
    return `${baseUrl}${logoPath}`;
  };

  const showEditLoading = isEditMode && (isSystemAdmin ? loadingAdmin : !currentAdmin);
  if (showEditLoading) {
    return (
      <div className="add-admin-main-container">
        <div className="add-admin-form-card" style={{ padding: '16px', display: 'grid', gap: '14px' }}>
          <FormDetailSkeleton titleLines={1} fieldRows={8} columns={2} showFooter={false} />
          <FormDetailSkeleton titleLines={1} fieldRows={2} columns={2} showFooter={true} />
        </div>
      </div>
    );
  }

  return (
    <>
      <AddFormModel
        title={isEditMode
          ? (isSystemAdmin ? 'Edit System Admin' : 'Edit Practice Admin')
          : (isSystemAdmin ? 'Add System Admin' : 'Add Practice Admin')
        }
      currentPractice={isSystemAdmin ? null : currentPractice}
      getLogoUrl={isSystemAdmin ? null : getLogoUrl}
      handleSubmit={handleSubmit}
      handleCancel={handleCancel}
      rows={buildPersonFormRows({
        formData,
        errors,
        isEditMode,
        handleInputChange,
        showPassword,
        showConfirmPassword,
        togglePasswordVisibility,
        toggleConfirmPasswordVisibility,
        phoneFieldName: 'phone',
        phoneLabel: 'Home Phone',
        phoneRequired: true,
      })}
      footerActions={
        <FormActionButtons
          onCancel={handleCancel}
          disabled={
            isSubmitting ||
            (isEditMode && !isChanged) ||
            Boolean(errors.password) ||
            Boolean(errors.confirmPassword) ||
            hasPasswordReuseError
          }
          isSubmitting={isSubmitting}
          isEditMode={isEditMode}
        />
      }
    />

    {/* Validation Warning Modal */}
    <Modal 
      show={showValidationModal} 
      onHide={() => setShowValidationModal(false)} 
      centered 
      className="add-admin-validation-modal"
    >
      <Modal.Body className="add-admin-modal-body" style={{ padding: '30px', textAlign: 'center' }}>
        <div className="add-admin-modal-icon-wrapper" style={{ marginBottom: '20px' }}>
          <FontAwesomeIcon icon={faInfoCircle} className="add-admin-modal-icon" style={{ fontSize: '40px', color: '#1A252F' }} />
        </div>
        <h4 className="add-admin-modal-title" style={{ color: '#1A252F', marginBottom: '20px', fontWeight: '600' }}>Fill all required fields</h4>
        <button 
          type="button"
          className="add-admin-modal-btn" 
          onClick={() => setShowValidationModal(false)}
          style={{ backgroundColor: '#1A252F', color: '#fff', border: 'none', padding: '10px 30px', borderRadius: '4px', cursor: 'pointer', width: '100%' }}
        >
          OK
        </button>
      </Modal.Body>
    </Modal>
  </>
  );
};

export default AddAdmin;